import { fn } from '@storybook/test';
import { useState } from 'react';

import type { ChangeEvent, PropsWithChildren } from 'react';
import type { Meta, StoryFn } from '@storybook/react';
import type { MultiSelectProps } from './MultiSelect';

import { MultiSelect } from './MultiSelect';

import { Container } from 'storyComponents/Container';

const codeString = `
  // MultiSelectHeader can be added optionally
  <MultiSelect.MultiSelectHeader label={label} clearTrigger={clearTrigger} onClear={handleClear} />
  <MultiSelect noBorder={noBorder} scrollable={scrollable}>
    {mockItems.map(({ itemName, itemValue, disabled }) => (
      <MultiSelect.MultiSelectItem key={itemName} label={itemName} value={itemValue}
        checked={stateItems.includes(itemValue)} onChange={handleChange} disabled={disabled} />
    ))}
  </MultiSelect>
  `;

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Organisms/MultiSelect',
  component: MultiSelect,
  argTypes: {
    header: {
      table: {
        disable: true,
      },
    },
  },
  parameters: {
    inspectComponent: MultiSelect,
    codeString: codeString,
  },
} as Meta<PropsWithChildren<MultiSelectProps>>;

const Template: StoryFn<PropsWithChildren<MultiSelectProps>> = (args) => {
  const [items, setItems] = useState<string[]>([]);

  const mockItems = [
    { itemName: 'AItem 1', itemValue: '1', disabled: true },
    { itemName: 'BItem 2', itemValue: '2' },
    { itemName: 'Multi line Item 3 Item 3 Item 3 Item 3 Item 3 Item 3 Item 3 Item 3 Item 3 Item 3', itemValue: '3' },
    { itemName: 'CItem 4', itemValue: '4' },
    { itemName: 'DItem 5', itemValue: '5' },
    { itemName: 'EItem 6', itemValue: '6' },
  ];

  const handleClear = () => {
    setItems([]);
    fn()();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    setItems((prevState) => {
      const newItems = checked ? [...prevState, value] : prevState.filter((itemValue) => itemValue !== value);
      fn()(newItems);
      return newItems;
    });
  };

  return (
    <Container>
      {/* header added under separate boolean arg here for storybook purpose, 
      it should be added as direct arg to MultiSelect when used in application */}
      {args.header && (
        <MultiSelect.MultiSelectHeader
          label="Item list"
          clearTrigger={items.length > 0 ? 'Clear Selection' : ''}
          onClear={handleClear}
        />
      )}
      <MultiSelect {...args}>
        {mockItems.map(({ itemName, itemValue, disabled }) => (
          <MultiSelect.MultiSelectItem
            key={itemName}
            label={itemName}
            value={itemValue}
            checked={items.includes(itemValue)}
            onChange={handleChange}
            disabled={disabled}
          />
        ))}
      </MultiSelect>
    </Container>
  );
};

const defaultArgs: Partial<MultiSelectProps> = {
  scrollable: true,
  noBorder: false,
};

export const MultiSelectExample = Template.bind({});
MultiSelectExample.args = {
  ...defaultArgs,
};

export const WithHeader = Template.bind({});
WithHeader.args = {
  ...defaultArgs,
  // boolean is not normal way of adding header but if added after template the rerender logic of
  // storybook would not correctly clear the checkboxes
  header: true,
};

export const WithheaderAndWithoutScrollContainerAndBorder = Template.bind({});
WithheaderAndWithoutScrollContainerAndBorder.args = {
  scrollable: false,
  noBorder: true,
  // boolean is not normal way of adding header but if added after template the rerender logic of
  // storybook would not correctly clear the checkboxes
  header: true,
};
